import { Component, OnInit, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import type { IFilterValues } from '@pages/reports/types/filter-values.interface';
import type { IFilterItem } from '@pages/reports/types/report-config.interface';
import { ReportFiltersStateService } from '@services/report-filters-state.service';
import { ReportSingleService } from '@services/report-single.service';
import { ReportOpportunitySearchFormEditorComponent } from '@shared/form-editors/report/report-opportunity-search-form-editor/report-opportunity-search-form-editor.component';
import { ReportOrganizationFormEditorComponent } from '@shared/form-editors/report/report-organization-form-editor/report-organization-form-editor.component';
import { ReportProjectSearchFormEditorComponent } from '@shared/form-editors/report/report-project-search-form-editor/report-project-search-form-editor.component';
import { ReportUserFormEditorComponent } from '@shared/form-editors/report/report-user-form-editor/report-user-form-editor.component';

@Component({
    selector: 'app-report-main-filters-form',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        ReportOrganizationFormEditorComponent,
        ReportUserFormEditorComponent,
        ReportProjectSearchFormEditorComponent,
        ReportOpportunitySearchFormEditorComponent
    ],
    templateUrl: './report-main-filters-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./report-main-filters-form.component.scss']
})
export class ReportMainFiltersFormComponent implements OnInit {
    readonly EditorType = EditorType;
    readonly form = new FormGroup({
        organizationIds: new FormControl<number[] | null>(null),
        salesPersonIds: new FormControl<number[] | null>(null),
        projectIds: new FormControl<number[] | null>(null),
        opportunityIds: new FormControl<number[] | null>(null)
    });

    private readonly service = inject(ReportSingleService);
    private readonly reportFiltersStateService = inject(ReportFiltersStateService);
    private syncingFromService = false;

    constructor() {
        effect(() => {
            const values = this.service.filterValues();
            this.syncingFromService = true;
            this.form.patchValue(
                {
                    organizationIds: values.organizationIds ?? null,
                    salesPersonIds: values.salesPersonIds ?? null,
                    projectIds: values.projectIds ?? null,
                    opportunityIds: values.opportunityIds ?? null
                },
                { emitEvent: false }
            );
            this.syncingFromService = false;
        });
    }

    ngOnInit(): void {
        this.form.valueChanges.subscribe((value) => {
            if (this.syncingFromService) return;

            const mainFilters: Partial<IFilterValues> = {};
            const activeMainFilters = this.service.configuration()?.filters.main ?? [];
            for (const item of activeMainFilters) {
                const key = this.getControlName(item) as keyof IFilterValues;
                const currentValue = value[key as keyof typeof value];
                mainFilters[key] = (currentValue ?? null) as any;
            }

            this.service.updateFilterValues(mainFilters);
            this.reportFiltersStateService.setMainFilters(mainFilters);
        });
    }

    getActiveMainFilters(): IFilterItem[] {
        return this.service.configuration()?.filters.main ?? [];
    }

    getControlName(item: IFilterItem): string {
        return item.fieldName;
    }

    getPlaceholder(item: IFilterItem): string {
        const custom = (item as IFilterItem & { additionalInfo?: { placeholder?: string } }).additionalInfo
            ?.placeholder;
        if (custom) return custom;
        return '';
    }
}
